import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Wrench, CheckCircle, AlertTriangle } from "lucide-react";

/**
 * One-click admin page to run the Stripe catalog setup against whatever
 * STRIPE_SECRET_KEY is loaded on the running backend. Fixes "Unknown price"
 * errors after env drift between container and prod.
 */
export default function AdminStripeRepair() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const run = async () => {
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/admin/stripe/setup");
      setResult(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-neutral-900 border border-neutral-800 p-8 text-center">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
          <div className="display text-2xl">Admin only</div>
          <div className="text-sm text-neutral-400 mt-2">
            Log in as an admin to use this page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="stripe-repair-page" className="min-h-screen bg-neutral-950">
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-12">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-[2px] w-10 bg-amber-500" />
          <span className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">
            Admin utility
          </span>
        </div>
        <h1 className="display text-4xl md:text-5xl">Repair Stripe Products</h1>
        <p className="text-neutral-300 mt-4 max-w-lg">
          This creates every SiteCrews product & price on the Stripe account
          your production server is currently connected to. Safe to run
          repeatedly — idempotent.
        </p>

        <div className="mt-6 bg-neutral-900 border border-neutral-800 p-5 text-sm text-neutral-400 leading-relaxed">
          <div className="text-[10px] uppercase tracking-widest text-amber-500 font-bold mb-2">
            What this fixes
          </div>
          <ul className="space-y-1">
            <li>· "Unknown price: contact_reveal_one" on Pay-per-contact</li>
            <li>· "Unknown price: founding_member" on the Founding banner</li>
            <li>· Any other missing price / product after env drift</li>
          </ul>
        </div>

        <button
          data-testid="run-stripe-repair"
          onClick={run}
          disabled={busy}
          className="mt-8 inline-flex items-center gap-3 bg-amber-500 text-neutral-950 px-8 py-4 font-bold uppercase tracking-widest text-sm hover:bg-amber-400 transition-colors disabled:opacity-60"
        >
          <Wrench className="w-4 h-4" />
          {busy ? "Repairing…" : "Repair Stripe Now"}
        </button>

        {error && (
          <div
            data-testid="stripe-repair-error"
            className="mt-6 border border-red-800 bg-red-950/40 text-red-300 px-4 py-3 text-sm"
          >
            {error}
          </div>
        )}

        {result?.ok && (
          <div data-testid="stripe-repair-success" className="mt-6">
            <div className="flex items-center gap-2 text-emerald-400 mb-3">
              <CheckCircle className="w-5 h-5" />
              <span className="font-bold uppercase tracking-widest text-sm">
                Done — payments should work now
              </span>
            </div>
            <div className="bg-neutral-900 border border-neutral-800 p-4 font-mono text-[11px] text-neutral-300 leading-relaxed max-h-96 overflow-y-auto">
              {(result.log || []).map((line, i) => (
                <div key={i} className={line.startsWith("price ok") ? "text-amber-400" : ""}>
                  {line}
                </div>
              ))}
            </div>
            <div className="mt-4 text-xs text-neutral-500 uppercase tracking-widest">
              Try the payment flow again — it should hit Stripe checkout instead of erroring.
            </div>
          </div>
        )}

        <BroadcastFounding />
      </div>
    </div>
  );
}

function BroadcastFounding() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const run = async () => {
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/admin/broadcast/founding");
      setResult(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-12 pt-8 border-t border-neutral-800">
      <div className="flex items-center gap-2 mb-3">
        <div className="h-[2px] w-10 bg-amber-500" />
        <span className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">
          Growth · one-tap
        </span>
      </div>
      <h2 className="display text-3xl">Broadcast — Founding Offer</h2>
      <p className="text-neutral-300 mt-3 max-w-lg">
        Sends every real user who hasn't already claimed Founding Member an email
        pushing the €29 lifetime offer with the live "X spots left" count.
        Idempotent — safe to run multiple times, each user gets it once.
      </p>
      <div className="mt-6 bg-amber-500/10 border-l-2 border-amber-500 pl-4 py-3 text-xs uppercase tracking-widest text-amber-500">
        ⚠️ Delivery requires Resend domain verification. Until then, emails to
        real gmails will be rejected by Resend sandbox.
      </div>
      <button
        data-testid="run-broadcast-founding"
        onClick={run}
        disabled={busy}
        className="mt-6 inline-flex items-center gap-3 bg-amber-500 text-neutral-950 px-8 py-4 font-bold uppercase tracking-widest text-sm hover:bg-amber-400 transition-colors disabled:opacity-60"
      >
        {busy ? "Sending…" : "Send Founding Blast Now"}
      </button>
      {error && (
        <div className="mt-4 border border-red-800 bg-red-950/40 text-red-300 px-4 py-3 text-sm">
          {error}
        </div>
      )}
      {result?.ok && (
        <div data-testid="broadcast-founding-success" className="mt-6 bg-neutral-900 border border-neutral-800 p-5 font-mono text-[12px] text-neutral-300 leading-relaxed">
          <div className="text-emerald-400 font-bold mb-2">Broadcast complete</div>
          <div>Sent: <span className="text-amber-400">{result.sent}</span></div>
          <div>Failed (Resend sandbox likely): <span className="text-amber-400">{result.failed}</span></div>
          <div>Skipped (seed/already claimed): <span className="text-neutral-500">{result.skipped}</span></div>
          <div>Spots left: <span className="text-amber-400">{result.remaining_spots}</span></div>
        </div>
      )}
    </div>
  );
}

